import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiSearch, FiMapPin, FiBriefcase, FiUsers, FiTrendingUp, FiShield, FiArrowRight, FiCheck } from 'react-icons/fi';
import { jobsAPI, companiesAPI } from '../services/api';
import { formatSalary, truncate, timeAgo } from '../utils/helpers';
import './Landing.css';

const Landing = () => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState('');
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [topCompanies, setTopCompanies] = useState([]);
  const [stats, setStats] = useState({ jobs: 0, companies: 0, candidates: 0 });

  useEffect(() => {
    const load = async () => {
      try {
        const [jobsRes, companiesRes] = await Promise.allSettled([
          jobsAPI.list({ limit: 6 }),
          companiesAPI.list({ limit: 6 }),
        ]);
        if (jobsRes.status === 'fulfilled') {
          const d = jobsRes.value.data;
          setFeaturedJobs(d.data || d.jobs || d.results || (Array.isArray(d) ? d : []));
          setStats((s) => ({ ...s, jobs: d.total || d.count || 250 }));
        }
        if (companiesRes.status === 'fulfilled') {
          const d = companiesRes.value.data;
          setTopCompanies(d.data || d.companies || d.results || (Array.isArray(d) ? d : []));
          setStats((s) => ({ ...s, companies: d.total || d.count || 45 }));
        }
      } catch (e) {
        // Use fallback stats
      }
    };
    load();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (keyword) params.set('q', keyword);
    if (location) params.set('location', location);
    navigate(`/jobs?${params.toString()}`);
  };

  return (
    <div className="landing">
      {/* Hero */}
      <section className="hero">
        <div className="hero-bg-grid" />
        <div className="hero-glow" />
        <div className="container hero-content">
          <div className="hero-badge">🇿🇦 South Africa's Employment Platform</div>
          <h1>Find Your Next<br /><span className="hero-gradient-text">Career Move</span></h1>
          <p className="hero-subtitle">
            No social feeds. No distractions. Just opportunities that match your skills, 
            experience, and ambitions. Connect directly with employers who are hiring now.
          </p>

          <form className="hero-search" onSubmit={handleSearch}>
            <div className="hero-search-field">
              <FiSearch className="search-icon" />
              <input
                type="text" placeholder="Job title, skill, or keyword..."
                value={keyword} onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
            <div className="hero-search-field">
              <FiMapPin className="search-icon" />
              <input
                type="text" placeholder="City or province..."
                value={location} onChange={(e) => setLocation(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-lg hero-search-btn">
              Search Jobs
            </button>
          </form>

          <div className="hero-tags">
            <span className="hero-tag-label">Popular:</span>
            {['Software Developer', 'Data Analyst', 'Project Manager', 'Accountant', 'Marketing'].map((t) => (
              <Link key={t} to={`/jobs?q=${encodeURIComponent(t)}`} className="hero-tag">{t}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="stats-bar">
        <div className="container-wide">
          <div className="stats-grid">
            <div className="stat-item">
              <FiBriefcase className="stat-icon" />
              <div><div className="stat-number">{stats.jobs || '250'}+</div><div className="stat-text">Active Jobs</div></div>
            </div>
            <div className="stat-item">
              <FiUsers className="stat-icon" />
              <div><div className="stat-number">{stats.companies || '45'}+</div><div className="stat-text">Companies</div></div>
            </div>
            <div className="stat-item">
              <FiTrendingUp className="stat-icon" />
              <div><div className="stat-number">1,200+</div><div className="stat-text">Candidates Placed</div></div>
            </div>
            <div className="stat-item">
              <FiShield className="stat-icon" />
              <div><div className="stat-number">POPIA</div><div className="stat-text">Compliant</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Jobs */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div>
              <h2>Featured Opportunities</h2>
              <p>Latest positions from top employers across South Africa</p>
            </div>
            <Link to="/jobs" className="btn btn-secondary">View All Jobs <FiArrowRight /></Link>
          </div>
          <div className="jobs-grid">
            {(featuredJobs.length > 0 ? featuredJobs : placeholderJobs).map((job, i) => (
              <Link to={`/jobs/${job.id || i}`} className="job-card" key={job.id || i} style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="job-card-top">
                  <div className="job-company-logo">{(job.company_name || job.company || 'C')[0]}</div>
                  <div>
                    <div className="job-company-name">{job.company_name || job.company || 'Company'}</div>
                    <div className="job-location"><FiMapPin />{job.location_name || job.location || 'South Africa'}</div>
                  </div>
                </div>
                <h3 className="job-title">{job.title || `Position ${i + 1}`}</h3>
                <p className="job-desc">{truncate(job.description || job.summary || 'An exciting opportunity at a leading company.', 100)}</p>
                <div className="job-card-footer">
                  <span className="badge">{job.job_type || job.type || 'Full-time'}</span>
                  <span className="job-salary">{formatSalary(job.salary_min, job.salary_max)}</span>
                </div>
                {job.created_at && <div className="job-posted">{timeAgo(job.created_at)}</div>}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="section how-it-works">
        <div className="container">
          <div className="section-header center">
            <h2>How PlacementHunter Works</h2>
            <p>Three simple steps to your next career move</p>
          </div>
          <div className="steps-grid">
            {[
              { num: '01', title: 'Create Your Profile', desc: 'Sign up and build your professional profile. Add your skills, experience, and what you\'re looking for. Your data is protected under POPIA.', icon: '👤' },
              { num: '02', title: 'Discover Opportunities', desc: 'Our matching algorithm finds jobs that align with your skills, preferred location, and salary expectations. No endless scrolling through irrelevant posts.', icon: '🔍' },
              { num: '03', title: 'Apply & Connect', desc: 'Apply directly to positions with one click. Track your applications in real-time and communicate with recruiters — all in one place.', icon: '🤝' },
            ].map((step, i) => (
              <div className="step-card" key={i} style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="step-num">{step.num}</div>
                <div className="step-icon">{step.icon}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Employers */}
      <section className="section employer-section">
        <div className="container">
          <div className="employer-grid">
            <div className="employer-content">
              <div className="hero-badge" style={{ display: 'inline-block' }}>For Employers</div>
              <h2>Find the Right Talent, Faster</h2>
              <p>
                PlacementHunter is built for efficient hiring. Post jobs, review applications 
                through a streamlined pipeline, and connect with pre-screened candidates 
                who match your requirements.
              </p>
              <ul className="employer-features">
                {[
                  'Smart candidate matching based on skills and experience',
                  'Application pipeline with status tracking',
                  'Company profile to showcase your employer brand',
                  'POPIA-compliant data handling throughout',
                  'Only see candidates actively looking for opportunities',
                ].map((f, i) => (
                  <li key={i}><FiCheck className="check-icon" /> {f}</li>
                ))}
              </ul>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <Link to="/register?role=recruiter" className="btn btn-primary">Start Hiring</Link>
                <Link to="/pricing" className="btn btn-secondary">View Pricing</Link>
              </div>
            </div>
            <div className="employer-visual">
              <div className="visual-card">
                <div className="visual-card-header">Recruiter Dashboard</div>
                <div className="visual-stat-row">
                  <div className="visual-stat"><span>12</span>Active Jobs</div>
                  <div className="visual-stat"><span>87</span>Applicants</div>
                  <div className="visual-stat"><span>5</span>Interviews</div>
                </div>
                <div className="visual-pipeline">
                  <div className="pipeline-bar" style={{ width: '85%', background: 'var(--gradient-primary)' }}>Applied (42)</div>
                  <div className="pipeline-bar" style={{ width: '60%', background: 'var(--gradient-accent)' }}>Reviewing (28)</div>
                  <div className="pipeline-bar" style={{ width: '35%', background: 'var(--accent-success)', opacity: 0.85 }}>Shortlisted (12)</div>
                  <div className="pipeline-bar" style={{ width: '15%', background: 'var(--accent-warning)', opacity: 0.85 }}>Interview (5)</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-card">
            <h2>Ready to Take the Next Step?</h2>
            <p>Whether you're seeking new opportunities or looking for exceptional talent, PlacementHunter has you covered.</p>
            <div className="cta-buttons">
              <Link to="/register" className="btn btn-primary btn-lg">Find a Job</Link>
              <Link to="/register?role=recruiter" className="btn btn-secondary btn-lg">Hire Talent</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Placeholder data when API isn't connected yet
const placeholderJobs = [
  { id: 1, title: 'Senior Software Developer', company_name: 'TechCorp SA', location: 'Johannesburg, Gauteng', job_type: 'Full-time', salary_min: 650000, salary_max: 900000, description: 'Join our engineering team to build cutting-edge fintech solutions for the African market.' },
  { id: 2, title: 'Data Analyst', company_name: 'Analytics Co', location: 'Cape Town, Western Cape', job_type: 'Full-time', salary_min: 400000, salary_max: 550000, description: 'Analyze complex datasets and deliver actionable insights to drive business decisions.' },
  { id: 3, title: 'Project Manager', company_name: 'BuildRight', location: 'Pretoria, Gauteng', job_type: 'Contract', salary_min: 500000, salary_max: 750000, description: 'Lead cross-functional teams delivering enterprise software projects on time and within scope.' },
  { id: 4, title: 'UX Designer', company_name: 'DesignHub', location: 'Durban, KwaZulu-Natal', job_type: 'Full-time', salary_min: 350000, salary_max: 500000, description: 'Design intuitive user experiences for web and mobile applications serving millions of users.' },
  { id: 5, title: 'Financial Accountant', company_name: 'FinServe Group', location: 'Sandton, Gauteng', job_type: 'Full-time', salary_min: 450000, salary_max: 600000, description: 'Manage financial reporting and compliance for a growing financial services group.' },
  { id: 6, title: 'DevOps Engineer', company_name: 'CloudFirst', location: 'Remote, South Africa', job_type: 'Full-time', salary_min: 600000, salary_max: 850000, description: 'Build and maintain CI/CD pipelines and cloud infrastructure on AWS and Azure.' },
];

export default Landing;
